
import { defineComponent } from '@vue/composition-api';
import {
  SportLiveItem,
  LiveEventsCountResult
} from '../../../../../types/sport';
import SportIcons from '@/components/IllinoisSport/components/SportIcons.vue';
import ChangePageModal from '@/components/IllinoisSport/components/ChangePageModal.vue';
import { formatSecondsToTime } from '@/composables/utils';
import EventItem from '../EventItem.vue';
import EventGroup from '../EventGroup.vue';

export default defineComponent({
  name: 'SidebarLive',
  components: {
    SportIcons,
    ChangePageModal,
    EventItem,
    EventGroup
  },
  props: {},
  computed: {
    sportLive(): SportLiveItem[] {
      const list = (this.$store.getters[
        'IllinoisSport/getLiveSportsAvailable'
      ] || []) as SportLiveItem[];
      return Array.isArray(list) ? list : [];
    },
    liveEventsBySport(): LiveEventsCountResult | null {
      if (!this.selectedSportId) return null;
      const getter = (this.$store.getters as any)[
        'IllinoisSport/getLiveEventsBySport'
      ];
      const res =
        typeof getter === 'function' ? getter(this.selectedSportId) : null;
      return (res || null) as LiveEventsCountResult | null;
    },
    computedModalHeight(): number {
      return this.runtimeHeight != null
        ? this.runtimeHeight
        : this.desiredModalHeight;
    },
    modalInlineStyle(): Record<string, string> {
      return {
        position: 'fixed',
        top: `${this.modalTop}px`,
        left: `${this.modalLeft}px`,
        height: `${this.computedModalHeight}px`,
        maxHeight: `calc(100vh - 96px)`
      } as any;
    },
    eventId(): string | null {
      return this.$route.params.eventId as string | null;
    },
    sportId(): string | null {
      return this.$route.params.sportId as string | null;
    },
    categoryId(): string | null {
      return this.$route.params.categoryId as string | null;
    },
    tournamentId(): string | null {
      return this.$route.params.tournamentId as string | null;
    }
  },
  data() {
    return {
      isOpen: false as boolean,
      selectedSportId: null as string | null,
      anchorButtonEl: null as HTMLElement | null,
      modalTop: 0 as number,
      modalLeft: 0 as number,
      desiredModalHeight: 600 as number, // circa h-150 (37.5rem)
      runtimeHeight: null as number | null
    };
  },
  methods: {
    formatSecondsToTime,
    onSportClick(sport: SportLiveItem, evt: MouseEvent) {
      this.selectedSportId = String(sport.id);
      this.isOpen = true;
      this.anchorButtonEl = evt.currentTarget as HTMLElement;

      // Carica dati se mancanti
      const hasData = this.$store.getters['IllinoisSport/getLiveEventsBySport'](
        this.selectedSportId
      );
      if (!hasData) {
        this.$store.dispatch('IllinoisSport/fetchLiveEventsCountBySport', {
          sportId: Number(this.selectedSportId)
        });
      }

      this.$nextTick(() => {
        this.recomputePosition();
      });
    },
    recomputePosition() {
      if (!this.anchorButtonEl) return;
      const rect = this.anchorButtonEl.getBoundingClientRect();
      const viewportH =
        window.innerHeight || document.documentElement.clientHeight;
      const viewportW =
        window.innerWidth || document.documentElement.clientWidth;

      // Vincoli 48px top/bottom e crescita verso l'alto se necessario
      const minMargin = 48;
      const maxHeight = viewportH - 2 * minMargin;
      const desiredHeight = Math.min(this.desiredModalHeight, maxHeight);
      const availableBelow = viewportH - minMargin - rect.top;

      let top = rect.top;
      let height = desiredHeight;

      if (availableBelow >= desiredHeight) {
        // Spazio sufficiente sotto: allinea il top al bottone
        top = rect.top;
        height = desiredHeight;
      } else {
        // Raggiunto il margine inferiore: fissa il bottom a 48px e cresci verso l'alto
        const deficit = desiredHeight - availableBelow; // quanto manca sotto
        top = rect.top - deficit; // sposta verso l'alto
        if (top < minMargin) {
          top = minMargin;
        }
        height = Math.min(desiredHeight, viewportH - minMargin - top);
      }

      // Posiziona a destra della sidebar
      let left = rect.right + 8;
      // Evita overflow a destra: se non c'è spazio, attaccalo comunque vicino alla sidebar
      if (left + 500 > viewportW) {
        left = Math.max(rect.right + 8, 8);
      }

      this.modalTop = Math.round(top);
      this.modalLeft = Math.round(left);
      this.runtimeHeight = Math.round(height);
    },
    closeModal() {
      this.isOpen = false;
      this.selectedSportId = null;
      this.anchorButtonEl = null;
    }
  },
  mounted() {
    window.addEventListener('resize', this.recomputePosition, {
      passive: true
    });
    window.addEventListener('scroll', this.recomputePosition, {
      passive: true
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.recomputePosition as any);
    window.removeEventListener('scroll', this.recomputePosition as any);
  },
  watch: {
    isOpen(val: boolean) {
      if (val) this.$nextTick(this.recomputePosition);
    },
    eventId(newVal) {
      if (newVal) {
        this.closeModal();
      }
    }
  }
});
